import { useCallback, useRef, useState } from "react";
import {
  arrayUnion,
  doc,
  runTransaction,
  serverTimestamp,
  type Firestore,
} from "firebase/firestore";

type ItemWithDelete = {
  id: string;
  deletePending?: boolean;
  deleteMarkedBy?: string | null;
  deleteMarkedAt?: string | null;
  auditLog?: any[];
  completed?: boolean;
  invoiceReportId?: string | null;
  lockedBy?: string | null;
  lockedAt?: string | null;
  updatedAt?: any;
};

type Params<T extends ItemWithDelete> = {
  db: Firestore;
  toMillis: (val: any) => number | null;
  LOCK_STALE_MS: number;
  currentUserString: () => string | null;
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  alertFn?: (msg: string) => void;
};

/**
 * Handles mark/unmark for delete from list with the same guards as the inline App implementation.
 */
export function useToggleDeleteFromList<T extends ItemWithDelete>({
  db,
  toMillis,
  LOCK_STALE_MS,
  currentUserString,
  setItems,
  alertFn = alert,
}: Params<T>) {
  const [deleteTogglePendingIds, setDeleteTogglePendingIds] = useState<Set<string>>(new Set());
  const deleteToggleBusyRef = useRef(false);

  const onToggleDeleteFromList = useCallback(async (item: T, toChecked: boolean) => {
    if (deleteToggleBusyRef.current) return;
    deleteToggleBusyRef.current = true;

    let rollbackItem: T | null = null;
    const nowIso = new Date().toISOString();
    const me = currentUserString();
    try {
      // Optimistisk uppdatering: uppdatera UI direkt
      setDeleteTogglePendingIds((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      setItems((prev: T[]) => {
        rollbackItem = prev.find((it) => it.id === item.id) ?? item;
        return prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                deletePending: toChecked,
                deleteMarkedBy: toChecked ? me : null,
                deleteMarkedAt: toChecked ? nowIso : null,
                updatedAt: nowIso,
              }
            : it
        );
      });

      const ref = doc(db, "itInventory", item.id);

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Posten finns inte längre.");
        const cur = snap.data() as any;

        if (cur.invoiceReportId) throw new Error("Kan inte ändra “ posten är fakturerad.");
        if (cur.completed) throw new Error("Kan inte ändra “ posten är markerad som färdig.");

        const heldByOther = !!cur.lockedBy && cur.lockedBy !== me;
        const lockedAtMs = toMillis(cur.lockedAt);
        const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
        if (heldByOther && !isStale) {
          throw new Error(`Posten redigeras av ${cur.lockedBy}.`);
        }

        if (cur.deletePending && cur.deleteMarkedBy && cur.deleteMarkedBy !== me) {
          throw new Error(`Redan markerad för radering av ${cur.deleteMarkedBy}.`);
        }

        if (!!cur.deletePending === !!toChecked) return;

        if (cur.deletePending && !toChecked && cur.deleteMarkedBy && cur.deleteMarkedBy !== me) {
          throw new Error(`Endast ${cur.deleteMarkedBy} kan avmarkera denna radering.`);
        }

        tx.update(ref, {
          deletePending: toChecked,
          deleteMarkedBy: toChecked ? me : null,
          deleteMarkedAt: toChecked ? nowIso : null,
          updatedAt: serverTimestamp(),
          auditLog: arrayUnion({
            action: toChecked ? "delete_marked" : "delete_unmarked",
            by: me,
            at: nowIso,
          }),
        } as any);
      });

      setItems((prev: T[]) =>
        prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                deletePending: toChecked,
                deleteMarkedBy: toChecked ? me : null,
                deleteMarkedAt: toChecked ? nowIso : null,
                updatedAt: nowIso,
                auditLog: Array.isArray((it as any).auditLog)
                  ? [...(it as any).auditLog, { action: toChecked ? "delete_marked" : "delete_unmarked", by: me, at: nowIso }]
                  : [{ action: toChecked ? "delete_marked" : "delete_unmarked", by: me, at: nowIso }],
              }
            : it
        )
      );
    } catch (e: any) {
      alertFn(e?.message || "Kunde inte ändra raderingsmarkeringen.");
      if (rollbackItem) {
        const previous = rollbackItem;
        setItems((prev: T[]) => prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                deletePending: previous.deletePending,
                deleteMarkedBy: previous.deleteMarkedBy,
                deleteMarkedAt: previous.deleteMarkedAt,
                updatedAt: previous.updatedAt,
              }
            : it
        ));
      }
    } finally {
      deleteToggleBusyRef.current = false;
      setDeleteTogglePendingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, [LOCK_STALE_MS, currentUserString, db, setItems, toMillis, alertFn]);

  return { onToggleDeleteFromList, deleteTogglePendingIds };
}
